import {
  Arrange,
  Container,
  SvgBulb as IcoBulb,
  SvgUser as IcoPersonal,
  SvgMoney as IcoPricing,
  SvgUsers as IcoShared,
  SvgStarOutline as IcoStarred,
  SvgDomain as IcoTeam,
  IconButton,
  Logo,
  Spacer,
  SvgClose,
} from '@loomhq/lens';
import classNames from 'classnames';
import React, { Suspense, useEffect, useState } from 'react';

import { ANON_NAV_ITEM_CLICKED, ANON_NAV_SHOWN } from 'constants/events';
import { EXP_PERSISTENT_RECORD_BUTTON_SIGNED_OUT } from 'constants/featureFlag';
import { PERSISTENT_RECORD_BUTTON_FTUX_TOOLTIP_DISMISSED } from 'constants/localStorage';
import {
  COMPANY,
  FAVORITES_EDUCATION_PAGE,
  MY_VIDEOS_EDUCATION_PAGE,
  PRICING,
  ROOT_PAGE,
  SHARED_VIDEOS_EDUCATION_PAGE,
  TEAM_VIDEOS_EDUCATION_PAGE,
  USE_CASES,
} from 'constants/routes';
import * as analytics from 'utilities/analytics';
import { usePersistentRecordButtonFTUXTooltipState } from 'utilities/localStorage/ftux';
import { isSdkSupported as importedIsSdkSupported } from 'utilities/sdk-support/isSdkSupported';

import { useFeatureFlagValue } from 'hooks/featureFlag';

import SignupCallout from './signup-callout';
import MenuListItem from '../navigation/menu-list-item';

import FTUXTooltip from 'components/ftux-tooltip';
import { SDK } from 'components/record-button/constants';
import PersistentRecordButton from 'components/record-button/persistent-record-button';

import styles from './styles.module.less';

const RecordButton = React.lazy(() => import('components/record-button'));

export const navBarAnimationDuration = 300;

const getPrimaryItems = () => [
  {
    icon: IcoPersonal,
    label: 'Personal',
    path: MY_VIDEOS_EDUCATION_PAGE,
    onClick: () =>
      analytics.track(ANON_NAV_ITEM_CLICKED, {
        path: MY_VIDEOS_EDUCATION_PAGE,
        label: 'Personal',
      }),
  },
  {
    icon: IcoShared,
    label: 'Shared with me',
    path: SHARED_VIDEOS_EDUCATION_PAGE,
    onClick: () =>
      analytics.track(ANON_NAV_ITEM_CLICKED, {
        path: SHARED_VIDEOS_EDUCATION_PAGE,
        label: 'Shared',
      }),
  },
  {
    icon: IcoTeam,
    label: 'Team',
    path: TEAM_VIDEOS_EDUCATION_PAGE,
    onClick: () =>
      analytics.track(ANON_NAV_ITEM_CLICKED, {
        path: TEAM_VIDEOS_EDUCATION_PAGE,
        label: 'Team',
      }),
  },
];

const getSecondaryItems = () => [
  {
    icon: IcoStarred,
    label: 'Starred',
    path: FAVORITES_EDUCATION_PAGE,
    onClick: () =>
      analytics.track(ANON_NAV_ITEM_CLICKED, {
        path: FAVORITES_EDUCATION_PAGE,
        label: 'Starred',
      }),
  },
];

const getTertiaryItems = () => [
  {
    icon: IcoBulb,
    label: 'Ways to use Loom',
    path: USE_CASES,
    onClick: () =>
      analytics.track(ANON_NAV_ITEM_CLICKED, {
        path: USE_CASES,
        label: 'Use Cases',
      }),
  },
  {
    icon: IcoPricing,
    label: 'Pricing',
    path: PRICING,
    onClick: () =>
      analytics.track(ANON_NAV_ITEM_CLICKED, {
        path: PRICING,
        label: 'Pricing',
      }),
  },
  {
    icon: IcoPersonal,
    label: 'About us',
    path: COMPANY,
    onClick: () =>
      analytics.track(ANON_NAV_ITEM_CLICKED, {
        path: COMPANY,
        label: 'About us',
      }),
  },
];

const AnonymousNavigation = ({ navBarIsOpenMobile, setNavBarIsOpenMobile }) => {
  const [isSdkSupported, setIsSdkSupported] = useState(false);
  const [skipRecordButtonFTUX, dismissRecordButtonFTUX] =
    usePersistentRecordButtonFTUXTooltipState();

  useEffect(() => {
    analytics.track(ANON_NAV_SHOWN);
    (async () => {
      const { supported } = await importedIsSdkSupported();

      setIsSdkSupported(supported);
    })();
  }, []);

  const PRIMARY_ITEMS = getPrimaryItems();
  const SECONDARY_ITEMS = getSecondaryItems();
  const TERTIARY_ITEMS = getTertiaryItems();

  const showPersistentRecordButton = useFeatureFlagValue(
    EXP_PERSISTENT_RECORD_BUTTON_SIGNED_OUT
  );

  return (
    <nav
      className={classNames(
        styles.mainNav,
        navBarIsOpenMobile && styles.navBarIsOpenMobile
      )}
      data-testid="anonymous-sidenav"
      tabIndex={0}
      style={{
        '--mainNavPadding': 'var(--lns-space-small)',
        '--iconAndLabelOffset': 'var(--lns-space-small)',
      }}
    >
      {navBarIsOpenMobile && (
        <IconButton
          className="ml:medium mt:medium"
          altText="Toggle library navigation"
          icon={<SvgClose />}
          onClick={() => setNavBarIsOpenMobile()}
        />
      )}
      <div className={styles.logoSection}>
        <a href={ROOT_PAGE}>
          <Logo maxWidth={12} />
        </a>
      </div>
      <Spacer top="medium" right="small" left="small">
        <SignupCallout />
      </Spacer>
      <Container paddingY="xlarge" overflow="auto">
        <Container borderSide="bottom" paddingBottom="large">
          <div className={styles.menuSectionTitle}>Libraries</div>
          <div className={styles.mainNavMenu}>
            <Arrange autoFlow="row" gap="2px" columns={['1fr']}>
              {PRIMARY_ITEMS.map(item => (
                <MenuListItem key={item.label} item={item} />
              ))}
              {SECONDARY_ITEMS.map(item => (
                <MenuListItem key={item.label} item={item} />
              ))}
            </Arrange>
          </div>
        </Container>
        <Spacer top="large" />
        <div className={styles.menuSectionTitle}>Learn more</div>
        <ul className={styles.mainNavMenu}>
          {TERTIARY_ITEMS.map(item => (
            <MenuListItem key={item.label} item={item} />
          ))}
        </ul>
      </Container>

      {showPersistentRecordButton === 'variant' &&
        isSdkSupported &&
        !navBarIsOpenMobile && (
          <div className="flex flexDirection:column absolute left:small bottom:small">
            {!skipRecordButtonFTUX && (
              <FTUXTooltip
                variantClassOverride={styles.ftuxTooltipExpanded}
                title="New! Start Recording"
                subtitle="Quickly record a video for free!"
                expanded
                dismissTooltip={dismissRecordButtonFTUX}
                source={PERSISTENT_RECORD_BUTTON_FTUX_TOOLTIP_DISMISSED}
              />
            )}
            <Container
              position="relative"
              left="calc(-1 * var(--lns-space-small))"
              paddingTop="small"
              paddingBottom={1.5}
            >
              <Suspense fallback={null}>
                <RecordButton priorityList={[SDK]}>
                  <PersistentRecordButton
                    isCollapsed={false}
                    onClick={dismissRecordButtonFTUX}
                  />
                </RecordButton>
              </Suspense>
            </Container>
          </div>
        )}
    </nav>
  );
};

export default AnonymousNavigation;
